import { useCallback, useEffect, useRef, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Key, Plus, ShieldCheck, ShieldAlert, Copy, Trash2, FileClock, FileDown, Eye, EyeOff } from "lucide-react";
import { API } from "@/lib/api";

function CreatedKeyDialog({ open, onClose, keyInfo }) {
  const copy = () => {
    navigator.clipboard.writeText(keyInfo?.key || "");
    toast.success("Copied");
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card border-white/10">
        <DialogHeader><DialogTitle>API key created</DialogTitle></DialogHeader>
        <div className="text-sm text-orange-300 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" /> Copy this key now. It will not be shown again.
        </div>
        <div className="rounded-md bg-black/60 border border-white/10 p-3 text-xs font-mono break-all" data-testid="api-key-value">
          {keyInfo?.key}
        </div>
        <Button data-testid="copy-key" onClick={copy} variant="outline" className="border-white/10">
          <Copy className="h-4 w-4 mr-1" /> Copy key
        </Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ApiAndAuditPage() {
  const [keys, setKeys] = useState([]);
  const [entries, setEntries] = useState([]);
  const [verify, setVerify] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState(null);
  // PII reveal — every flip re-fetches with ?reveal=true which is itself
  // audit-logged server-side (audit.reveal_pii). Admins only; the /audit
  // endpoint enforces role at the API layer. Auto-expires after 60s so a
  // walked-away admin doesn't leave PII on-screen.
  const REVEAL_TTL_SEC = 60;
  const [reveal, setReveal] = useState(false);
  const [revealCountdown, setRevealCountdown] = useState(0);
  const revealTimerRef = useRef(null);

  const loadKeys = useCallback(async () => {
    try { const { data } = await api.get("/api-keys"); setKeys(data.items || []); }
    catch { setKeys([]); }
  }, []);
  const loadAudit = useCallback(async (revealFlag = false) => {
    try {
      const { data } = await api.get(`/audit?limit=200${revealFlag ? "&reveal=true" : ""}`);
      setEntries(data.items || []);
    } catch { setEntries([]); }
  }, []);
  const runVerify = useCallback(async () => {
    try { const { data } = await api.get("/audit/verify"); setVerify(data); }
    catch { setVerify(null); }
  }, []);

  useEffect(() => { loadKeys(); loadAudit(false); runVerify(); }, [loadKeys, loadAudit, runVerify]);

  // Reveal-session timeout — after 30 min of no user interaction on this
  // page, force a full logout re-flow. Anyone who opens the audit page and
  // walks away shouldn't leave a live admin session sitting there.
  const IDLE_LOGOUT_MS = 30 * 60 * 1000;
  useEffect(() => {
    let idleT = null;
    const reset = () => {
      if (idleT) clearTimeout(idleT);
      idleT = setTimeout(() => {
        toast.warning("Session timeout", {
          description: "No activity for 30 minutes. You'll be redirected to sign in again.",
          duration: 4000,
        });
        setTimeout(() => {
          // Session lives in the httpOnly access_token cookie — hit
          // /api/auth/logout so the server drops it, then redirect.
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`,
                 { method: "POST", credentials: "include" })
            .catch(() => { /* offline — logout will fail closed */ })
            .finally(() => { window.location.href = "/login"; });
        }, 1500);
      }, IDLE_LOGOUT_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (idleT) clearTimeout(idleT);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [IDLE_LOGOUT_MS]);

  // Clean up the reveal countdown timer on unmount so we never leak.
  useEffect(() => () => {
    if (revealTimerRef.current) clearInterval(revealTimerRef.current);
  }, []);

  const stopRevealTimer = useCallback(() => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    setRevealCountdown(0);
  }, []);

  const startRevealTimer = useCallback(() => {
    stopRevealTimer();
    setRevealCountdown(REVEAL_TTL_SEC);
    revealTimerRef.current = setInterval(() => {
      setRevealCountdown((c) => {
        if (c <= 1) {
          clearInterval(revealTimerRef.current);
          revealTimerRef.current = null;
          // Auto-flip off so an idle screen doesn't keep PII exposed.
          setReveal(false);
          loadAudit(false);
          toast.info("Reveal PII auto-expired", {
            description: "The 60-second window ended. Toggle again if you still need to see raw values.",
            duration: 4000,
          });
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  }, [loadAudit, stopRevealTimer]);

  const toggleReveal = async (next) => {
    setReveal(next);
    await loadAudit(next);
    if (next) {
      startRevealTimer();
      toast.warning("PII revealed", {
        description: `This reveal is recorded in the audit log and auto-expires in ${REVEAL_TTL_SEC}s.`,
        duration: 4000,
      });
    } else {
      stopRevealTimer();
    }
  };

  const createKey = async () => {
    if (!newName.trim()) return toast.error("Give the key a name");
    setSaving(true);
    try {
      const { data } = await api.post("/api-keys", { name: newName.trim(), scopes: ["read:public"] });
      setCreated(data);
      setNewName("");
      setNewOpen(false);
      loadKeys();
      loadAudit(reveal);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this key immediately?")) return;
    try {
      await api.delete(`/api-keys/${id}`);
      toast.success("Revoked");
      loadKeys();
      loadAudit(reveal);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Platform"
        title="Public API & Audit Log"
        subtitle="Read-only API for external integrations. Every write is chained into a tamper-evident audit log."
      />

      <Tabs defaultValue="keys" className="w-full">
        <TabsList className="bg-white/[0.03] border border-white/10">
          <TabsTrigger value="keys" data-testid="tab-keys">API keys</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit log</TabsTrigger>
          <TabsTrigger value="docs" data-testid="tab-docs">Docs</TabsTrigger>
        </TabsList>

        <TabsContent value="keys" className="mt-4">
          <Card className="border-white/10 bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="overline">API keys</div>
              <Dialog open={newOpen} onOpenChange={setNewOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="new-api-key-btn" className="bg-orange-500 hover:bg-orange-600 rounded-full">
                    <Plus className="h-4 w-4 mr-1" /> New key
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-white/10">
                  <DialogHeader><DialogTitle>Create API key</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Name / integration</Label>
                      <Input data-testid="api-key-name" value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder="e.g. Concierge Kiosk" />
                    </div>
                    <div className="text-xs text-zinc-500">Scope: <code className="text-zinc-300">read:public</code></div>
                    <Button data-testid="save-api-key" onClick={createKey} disabled={saving}
                            className="w-full bg-orange-500 hover:bg-orange-600">
                      {saving ? "Creating…" : "Create key"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
            {keys.length === 0 ? (
              <div className="text-sm text-muted-foreground">No API keys yet.</div>
            ) : (
              <div className="space-y-2">
                {keys.map((k) => (
                  <div key={k.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3 flex items-center justify-between" data-testid={`apikey-${k.id}`}>
                    <div className="min-w-0">
                      <div className="font-medium truncate flex items-center gap-2">
                        <Key className="h-4 w-4 text-orange-400" /> {k.name}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-500">{k.prefix}…</div>
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                        {k.revoked_at ? "Revoked" : `Last used ${k.last_used_at ? new Date(k.last_used_at).toLocaleString() : "never"}`}
                      </div>
                    </div>
                    {!k.revoked_at && (
                      <button data-testid={`revoke-${k.id}`} onClick={() => revoke(k.id)} className="text-zinc-500 hover:text-rose-400">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="audit" className="mt-4">
          <Card className="border-white/10 bg-card p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="overline">Immutable audit log</div>
                <div className="text-xs text-zinc-500">Hash-chained · {entries.length} recent entries</div>
              </div>
              <div className="flex items-center gap-2" data-testid="audit-verify-status">
                {verify?.verified ? (
                  <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 bg-emerald-500/10">
                    <ShieldCheck className="h-3 w-3 mr-1" /> Chain verified · {verify.total_entries} entries
                  </Badge>
                ) : verify ? (
                  <Badge variant="outline" className="border-rose-500/40 text-rose-300 bg-rose-500/10">
                    <ShieldAlert className="h-3 w-3 mr-1" /> Chain broken · {verify.breaks?.length || 0} conflicts
                  </Badge>
                ) : null}
                <a href={`${API}/admin/audit/export.csv`} target="_blank" rel="noreferrer"
                   data-testid="audit-export-csv"
                   className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-white/10 text-zinc-300 hover:bg-white/5">
                  <FileDown className="h-3 w-3" /> Export CSV
                </a>
                <a href={`${API}/admin/audit/export.pdf`} target="_blank" rel="noreferrer"
                   data-testid="audit-export-pdf"
                   className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full border border-orange-500/40 text-orange-300 bg-orange-500/10 hover:bg-orange-500/20">
                  <FileDown className="h-3 w-3" /> Export PDF
                </a>
                <div className="inline-flex items-center gap-2 text-xs px-3 py-1 rounded-full border border-white/10 bg-white/[0.02]"
                     data-testid="audit-reveal-toggle-wrap"
                     title="Reveal the raw (unmasked) PII stored for each entry. Every reveal is audit-logged.">
                  {reveal
                    ? <Eye className="h-3 w-3 text-orange-300" />
                    : <EyeOff className="h-3 w-3 text-zinc-500" />}
                  <span className={reveal ? "text-orange-300" : "text-zinc-400"}>Reveal PII</span>
                  <Switch data-testid="audit-reveal-toggle" checked={reveal} onCheckedChange={toggleReveal} />
                </div>
                <Button data-testid="audit-verify-btn" size="sm" variant="outline" className="border-white/10" onClick={runVerify}>Re-verify</Button>
              </div>
            </div>
            {reveal && (
              <div className="mb-3 rounded-md border border-orange-500/30 bg-orange-500/5 px-3 py-2 text-[11px] text-orange-200 flex items-center justify-between gap-3"
                   data-testid="audit-reveal-banner">
                <div>
                  <ShieldAlert className="h-3.5 w-3.5 inline mr-1 -mt-0.5" />
                  PII is currently revealed. The masked view stays on the left; the original values appear on the right for entries created after this feature was enabled. This session has been recorded in the audit log.
                </div>
                {revealCountdown > 0 && (
                  <div className="whitespace-nowrap text-orange-100 font-mono text-[11px] rounded-full border border-orange-400/40 bg-orange-500/15 px-2 py-0.5"
                       data-testid="audit-reveal-countdown"
                       title="Reveal auto-expires when this hits 0">
                    auto-hide in {revealCountdown}s
                  </div>
                )}
              </div>
            )}
            <div className="space-y-2 max-h-[60vh] overflow-y-auto scroll-slim pr-1">
              {entries.map((e) => (
                <div key={e.id} className="rounded-md border border-white/10 bg-white/[0.02] p-3" data-testid={`audit-${e.seq}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="border-white/10 font-mono text-[10px]">seq {e.seq}</Badge>
                    <Badge variant="outline" className="border-indigo-500/40 text-indigo-300 bg-indigo-500/10">{e.action}</Badge>
                    <div className="text-sm">{e.actor_name} <span className="text-zinc-500">({e.actor_role})</span></div>
                    <div className="text-xs text-zinc-500 ml-auto flex items-center gap-1">
                      <FileClock className="h-3 w-3" /> {new Date(e.at).toLocaleString()}
                    </div>
                  </div>
                  {e.details && Object.keys(e.details).length > 0 && (
                    reveal ? (
                      <div className="grid md:grid-cols-2 gap-2 mt-2" data-testid={`audit-diff-${e.seq}`}>
                        <div className="rounded border border-white/5 bg-black/20 p-2">
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 flex items-center gap-1">
                            <EyeOff className="h-3 w-3" /> Masked
                          </div>
                          <pre className="text-[11px] text-zinc-400 whitespace-pre-wrap break-words">{JSON.stringify(e.details, null, 0)}</pre>
                        </div>
                        <div className="rounded border border-orange-500/20 bg-orange-500/[0.04] p-2">
                          <div className="text-[10px] uppercase tracking-widest text-orange-300 mb-1 flex items-center gap-1">
                            <Eye className="h-3 w-3" /> Original
                          </div>
                          <pre className="text-[11px] text-orange-100/90 whitespace-pre-wrap break-words" data-testid={`audit-original-${e.seq}`}>
                            {e.details_original
                              ? JSON.stringify(e.details_original, null, 0)
                              : <span className="text-zinc-500 italic">(no raw copy — pre-feature entry)</span>}
                          </pre>
                        </div>
                      </div>
                    ) : (
                      <pre className="text-[11px] text-zinc-400 mt-2 whitespace-pre-wrap break-words">{JSON.stringify(e.details, null, 0)}</pre>
                    )
                  )}
                  <div className="text-[10px] font-mono text-zinc-500 mt-1 break-all">hash: {e.hash?.slice(0, 32)}…</div>
                </div>
              ))}
              {entries.length === 0 && <div className="text-sm text-muted-foreground">No entries yet.</div>}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="docs" className="mt-4">
          <Card className="border-white/10 bg-card p-5">
            <div className="overline mb-2">Public API — quickstart</div>
            <p className="text-sm text-zinc-400 mb-3">
              Send the key in the <code className="text-zinc-300">X-API-Key</code> header. All endpoints
              are read-only and return non-sensitive data — no resident PII, no financial details.
            </p>
            <pre className="text-xs bg-black/60 border border-white/10 rounded-md p-3 overflow-x-auto">{`curl -H 'X-API-Key: <your key>' \\
  ${window.location.origin.replace(/\/$/, "")}/api/public/health

# Endpoints
GET /api/public/health
GET /api/public/properties
GET /api/public/vendors?property_id=&category=
GET /api/public/board-packets?property_id=`}</pre>
          </Card>
        </TabsContent>
      </Tabs>

      <CreatedKeyDialog open={!!created} onClose={() => setCreated(null)} keyInfo={created} />
    </div>
  );
}
